import React from 'react';
import {
  ClipboardCopyIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from 'lucide-react';
import { formatNumber } from './audioMatcherUtils';

function formatPhonemes(value) {
  return Array.isArray(value) && value.length > 0 ? value.join(' ') : '∅';
}

function formatErrorType(value) {
  return typeof value === 'string' && value
    ? value.replace(/_/g, ' ')
    : 'Unclassified';
}

function formatFeatureValue(value) {
  return typeof value === 'number' && value > 0 ? `+${value}` : String(value ?? '?');
}

export default function ResultSegment({
  segment,
  index,
  totalSegments,
  isExpanded,
  toggleJson,
  copyJson,
}) {
  const errors = Array.isArray(segment.errors) ? segment.errors : [];

  return (
    <article className={`segment-card ${segment.isInfo ? 'segment-info' : ''}`}>
      <div className="seg-header">
        <div>
          <span className="seg-title">Segment {totalSegments - index}</span>
          <span className="seg-subtitle">{segment.status}</span>
        </div>
        <div className="seg-actions">
          <button
            type="button"
            onClick={() => toggleJson(segment.id)}
            className="icon-btn"
            aria-label="Toggle JSON"
          >
            {isExpanded ? <ChevronUpIcon size={16} /> : <ChevronDownIcon size={16} />}
          </button>
          <button
            type="button"
            onClick={() => copyJson(segment.rawData)}
            className="icon-btn"
            aria-label="Copy JSON"
          >
            <ClipboardCopyIcon size={16} />
          </button>
        </div>
      </div>

      <div className="pill-row">
        {!segment.isInfo && <span className="pill">Accuracy {formatNumber(segment.accuracy, 3)}</span>}
        {!segment.isInfo && errors.length > 0 && (
          <span className="pill error-count-pill">
            {errors.length} {errors.length === 1 ? 'Error' : 'Errors'}
          </span>
        )}
        {segment.latency != null && <span className="pill">Latency {segment.latency} ms</span>}
        {segment.pitch != null && <span className="pill">Pitch {Math.round(segment.pitch)} Hz</span>}
        {segment.loudness != null && <span className="pill">Loudness {segment.loudness} dB</span>}
      </div>

      <div className="seg-body">
        {segment.isInfo ? (
          <div className="info-row">
            <p>{segment.rawData?.error || segment.rawData?.status || 'Info'}</p>
            {segment.rawData?.details && <p className="muted-copy">{segment.rawData.details}</p>}
          </div>
        ) : (
          <>
            {segment.transcript && (
              <div className="detail-row">
                <strong>Transcript</strong>
                <span className="mono">{segment.transcript}</span>
              </div>
            )}
            {segment.predicted && (
              <div className="detail-row">
                <strong>Predicted</strong>
                <span className="mono">{segment.predicted}</span>
              </div>
            )}
            {segment.expected && (
              <div className="detail-row">
                <strong>Expected</strong>
                <span className="mono">{segment.expected}</span>
              </div>
            )}
            {segment.errorAnalysisTarget && (
              <div className="detail-row error-analysis-target">
                <strong>Error Analysis Target</strong>
                <span className="mono">{segment.errorAnalysisTarget}</span>
              </div>
            )}
            {segment.actionWordLabel && (
              <div className="detail-row">
                <strong>Action Label</strong>
                <span>{segment.actionWordLabel}</span>
              </div>
            )}
            <div className="detail-grid">
              <div className="detail-box">
                <span className="detail-label">Chunk ID</span>
                <span className="mono">{segment.chunkId || '--'}</span>
              </div>
              <div className="detail-box">
                <span className="detail-label">Model</span>
                <span>{segment.modelId || '--'}</span>
              </div>
              <div className="detail-box">
                <span className="detail-label">Speech Rate</span>
                <span>{formatNumber(segment.speechRate)}</span>
              </div>
              <div className="detail-box">
                <span className="detail-label">Repetitions</span>
                <span>{segment.repetitions ?? '--'}</span>
              </div>
              <div className="detail-box">
                <span className="detail-label">Greedy Confidence</span>
                <span>{formatNumber(segment.greedyPathConfidence, 6)}</span>
              </div>
            </div>

            <div className="matches">
              <strong>Matches</strong>
              {segment.matches.length ? (
                <ul>
                  {segment.matches.map((match, matchIndex) => (
                    <li key={`${segment.id}-match-${matchIndex}`}>
                      {match.expected} ↔ {match.predicted} | {formatNumber(Number(match.match_score ?? 0))}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="muted-copy">No match details returned.</p>
              )}
            </div>

            {errors.length > 0 && (
              <section className="speech-errors" aria-label="Pronunciation errors">
                <div className="speech-errors-header">
                  <strong>Pronunciation Errors</strong>
                  <span>{errors.length}</span>
                </div>
                <div className="speech-error-list">
                  {errors.map((error, errorIndex) => {
                    const features = Array.isArray(error.panphon_features)
                      ? error.panphon_features.filter(
                        feature => feature && typeof feature === 'object'
                      )
                      : [];
                    return (
                      <article className="speech-error-card" key={`${segment.id}-error-${errorIndex}`}>
                        <div className="speech-error-title-row">
                          <span className="error-type">{formatErrorType(error.error_type)}</span>
                          <span className={`error-source ${error.common_mistake ? 'known' : 'unknown'}`}>
                            {error.common_mistake ? 'Common mistake' : 'Unclassified'}
                          </span>
                        </div>
                        <div className="speech-error-phonemes mono">
                          {formatPhonemes(error.expected_phonemes)}
                          <span aria-hidden="true">→</span>
                          {formatPhonemes(error.predicted_phonemes)}
                        </div>
                        <div className="speech-error-meta">
                          {error.error && <span>{formatErrorType(error.error)}</span>}
                          {error.position && <span>{formatErrorType(error.position)}</span>}
                          {error.feature_change && <span>{error.feature_change}</span>}
                        </div>
                        {features.length > 0 && (
                          <div className="panphon-features" aria-label="PanPhon feature changes">
                            {features.map((feature, featureIndex) => (
                              <span
                                className="panphon-feature"
                                key={`${segment.id}-error-${errorIndex}-feature-${featureIndex}`}
                              >
                                {feature.feature || '?'} {formatFeatureValue(feature.from_value)}
                                {' → '}
                                {formatFeatureValue(feature.to_value)}
                              </span>
                            ))}
                          </div>
                        )}
                      </article>
                    );
                  })}
                </div>
              </section>
            )}

            {segment.perPhonemeConfidence.length > 0 && (
              <div className="confidence-row">
                {segment.perPhonemeConfidence.map((entry, entryIndex) => (
                  <span className="confidence-pill" key={`${segment.id}-conf-${entryIndex}`}>
                    {entry.phoneme}: {formatNumber(Number(entry.conf), 3)}
                  </span>
                ))}
              </div>
            )}
          </>
        )}

        {isExpanded && (
          <div className="raw-json-wrap">
            <pre className="raw-json">{JSON.stringify(segment.rawData, null, 2)}</pre>
          </div>
        )}
      </div>
    </article>
  );
}
